import isEqual from 'react-fast-compare';
import {shallowEqual} from 'react-redux';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {makeReactContext} from 'lib/makeReactContext';
import {SectionConfiguration} from 'notebook/ELNConstants';
import {Note} from 'notebook/util/NoteUtilConstants';

export const getDefaultSectionConfigurationContext = (): SectionConfiguration => ({
  allows: {
    collapse: BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS,
    date: true,
    insertion: true,
    preamble: false,
    title: BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS,
  },
});

export const SectionConfigurationContext = makeReactContext<SectionConfiguration>(
  getDefaultSectionConfigurationContext,
  'SectionConfigurationContext'
);

/**
 * Simple setup for using context to hydrate the NoteEditor subtree with useful state.
 *
 * Two separate context objects are created here:
 *  - EditorPropertiesContext, which tracks some important metadata, and
 *  - NoteContext, which tracks the full note object
 *
 * The context values are separated to allow faster `isEqual` checks. See `makeReactContext`
 * for details.
 */

export interface EditorPropertiesContextInterface {
  showFormulas: boolean;
  highlightCustomColumns?: boolean;
  isConfig: boolean;
  rootFolderId?: string | null;
  templateCollectionId?: string | null;
  procedureId?: string;
  deprecatedStepCreationContextId?: string;
  fileId?: string;
  isExportView?: boolean;
  isPrintPreviewView?: boolean;
  disableSplitscreen?: boolean;
  autoHeightToolbar?: boolean;
  disablePerformanceIndicator?: boolean;
  disableProtectionIndicator?: boolean;
}

function _isExportViewRequiresIsPrintPreview({
  isExportView,
  isPrintPreviewView,
}: EditorPropertiesContextInterface): void {
  if (isExportView) {
    assertAndContinue(
      Boolean(isPrintPreviewView),
      'PrintPreviewView should always be active for isExportView'
    );
  }
}

export const EditorPropertiesContext = makeReactContext<EditorPropertiesContextInterface>(
  () => ({
    showFormulas: false,
    highlightCustomColumns: false,
    isPrintPreviewView: false,
    isExportView: false,
    isConfig: false,
  }),
  'EditorPropertiesContext',
  isEqual,
  {validationFn: _isExportViewRequiresIsPrintPreview}
);

/*
  WARNING: the following context value is
    - large
    - complex
    - ever-changing

  If you use this context in your component, your component will re-render EVERY TIME the note object changes.
  This happens _very_ frequently.  So do not consume this context in places where it will impact performance.

  If we run into a situation in which changes to the note (as a whole!) are required rapidly, or end up
  rendering too many NoteContext.Consumer components (this will wreck us!), we can reevaluate:
    * Break up the data which we broadcast to have fewer dependencies, as we did above in
      EditorPropertiesContext
    * Move the required data to the plugin's `getItemProps`, which avoids context issues altogether
    * Consider debouncing the equality check (which will be good for performance, but potentially make
      things hard to reason about)
*/

export interface NoteContextInterface {
  note?: Note;
}

/**
 * @deprecated NoteContext should not be used because it is not performant. Using this context will force
 * you to re-render any time anything in the note changes.
 *
 * If you need something from the note, you should either receive it passed down as props from the Editor or
 * directly grab it from the flux store with a selector to only grab what you need by using useStoreSelector.
 */
export const NoteContext = makeReactContext<NoteContextInterface>(() => ({}), 'NoteContext', shallowEqual);
